import {
  OutcomeDocument,
  OutcomeReadyResponse,
  OutcomeReadySubmission,
  OutcomeStandaloneDocument,
} from '../types';

/**
 * Hand-crafted submission groups mirroring the Figma design.
 *
 * Kept verbatim (and first in order) because several specs address the payload
 * positionally — `submissions[0].documents[0]`, `.slice(0, 3)` — so curated
 * entries must stay stable while the generated volume grows around them.
 *
 * @remarks `workspace_name` intentionally carries the `PROTO-…` value because the
 * design renders the sponsor protocol id as the group title.
 */
const CURATED_SUBMISSIONS: OutcomeReadySubmission[] = [
  {
    workspace_id: 'c4f1a2b0-0001-4f5a-9c1d-1a2b3c4d0001',
    workspace_name: 'PROTO-2024-077',
    study_name: 'Immunology biologics',
    sponsor_protocol_id: 'PROTO-2024-077',
    document_count: 3,
    documents: [
      {
        id: 'd1a0e5c2-0001-4a11-8b21-9f0a1b2c0001',
        title: 'Approval letter',
        document_type_name: 'Approval Letter',
        posted_date: '2026-08-08T10:00:00Z',
      },
      {
        id: 'd1a0e5c2-0002-4a11-8b21-9f0a1b2c0002',
        title: 'Consent form — stamped',
        document_type_name: 'Consent Form',
        posted_date: '2026-08-08T09:45:00Z',
      },
      {
        id: 'd1a0e5c2-0003-4a11-8b21-9f0a1b2c0003',
        title: 'Protocol synopsis',
        document_type_name: 'Protocol Synopsis',
        posted_date: '2026-08-07T16:20:00Z',
      },
    ],
  },
  {
    workspace_id: 'c4f1a2b0-0002-4f5a-9c1d-1a2b3c4d0002',
    workspace_name: 'PROTO-2024-169',
    study_name: 'Renal transplant study',
    sponsor_protocol_id: 'PROTO-2024-169',
    document_count: 2,
    documents: [
      {
        id: 'd1a0e5c2-0004-4a11-8b21-9f0a1b2c0004',
        title: 'Approval letter',
        document_type_name: 'Approval Letter',
        posted_date: '2026-08-06T11:05:00Z',
      },
      {
        id: 'd1a0e5c2-0005-4a11-8b21-9f0a1b2c0005',
        title: 'Assent form — stamped',
        document_type_name: 'Assent Form',
        posted_date: '2026-08-06T11:00:00Z',
      },
    ],
  },
  {
    workspace_id: 'c4f1a2b0-0003-4f5a-9c1d-1a2b3c4d0003',
    workspace_name: 'PROTO-2024-156',
    study_name: 'CNS protocol amendment',
    sponsor_protocol_id: 'PROTO-2024-156',
    document_count: 4,
    documents: [
      {
        id: 'd1a0e5c2-0006-4a11-8b21-9f0a1b2c0006',
        title: 'Conditional approval letter',
        document_type_name: 'Conditional Approval Letter',
        posted_date: '2026-08-05T14:30:00Z',
      },
      {
        id: 'd1a0e5c2-0007-4a11-8b21-9f0a1b2c0007',
        title: 'Stipulations summary',
        document_type_name: 'Stipulations Summary',
        posted_date: '2026-08-05T14:25:00Z',
      },
      {
        id: 'd1a0e5c2-0008-4a11-8b21-9f0a1b2c0008',
        title: 'Revised consent form',
        document_type_name: 'Consent Form',
        posted_date: '2026-08-04T09:10:00Z',
      },
      {
        id: 'd1a0e5c2-0009-4a11-8b21-9f0a1b2c0009',
        title: 'Review comments',
        document_type_name: 'Review Comments',
        posted_date: '2026-08-04T09:05:00Z',
      },
    ],
  },
];

/**
 * Hand-crafted standalone document, kept first for the same positional-spec
 * stability reason as {@link CURATED_SUBMISSIONS}.
 *
 * @remarks The standalone shape is unverified against the back end — see
 * `OutcomeStandaloneDocument`.
 */
const CURATED_STANDALONE_DOCUMENTS: OutcomeStandaloneDocument[] = [
  {
    id: 'd1a0e5c2-0010-4a11-8b21-9f0a1b2c0010',
    title: 'Review notes',
    document_type_name: 'Review Notes',
    posted_date: '2026-08-03T13:40:00Z',
    study_name: 'Phase II oncology trial',
    sponsor_protocol_id: 'PROTO-2024-118',
  },
];

/** Study names cycled across the generated submission groups. */
const GENERATED_STUDY_NAMES = [
  'Cardiology device registry',
  'Dermatology topical study',
  'Gastro biologics extension',
  'Neurology gene therapy',
  'Respiratory inhaler trial',
  'Rheumatology biosimilar study',
] as const;

/** Document types cycled across the generated documents. */
const GENERATED_DOCUMENT_TYPES = [
  'Approval Letter',
  'Consent Form',
  'Protocol Synopsis',
  'Review Comments',
  'Conditional Approval Letter',
  'Stipulations Summary',
] as const;

/**
 * Builds one deterministic generated outcome document.
 *
 * Everything derives from `sequence` (no randomness) so ids stay unique across
 * the whole mock and reruns produce byte-identical data.
 *
 * @param sequence - Global document sequence (1001-based, 4 digits) used for the
 * id, title, type, and posted date.
 * @returns A fully populated {@link OutcomeDocument}.
 */
function buildGeneratedDocument(sequence: number): OutcomeDocument {
  const _type =
    GENERATED_DOCUMENT_TYPES[sequence % GENERATED_DOCUMENT_TYPES.length];
  const _day = `${(sequence % 8) + 1}`.padStart(2, '0');
  const _hour = `${(sequence % 9) + 8}`.padStart(2, '0');

  return {
    id: `d1a0e5c2-${sequence}-4a11-8b21-9f0a1b2c${sequence}`,
    title: `${_type} ${sequence}`,
    document_type_name: _type,
    posted_date: `2026-08-${_day}T${_hour}:00:00Z`,
  };
}

/**
 * Builds the generated submission groups that push the mock past 100 documents.
 *
 * Group sizes cycle 3 → 4 → 5 documents so pagination boundaries land inside
 * groups of different shapes; per-group `document_count` is derived from the
 * built array so it can never drift.
 *
 * @param groupCount - Number of submission groups to generate.
 * @returns Deterministic {@link OutcomeReadySubmission} groups (ids start at 1001).
 */
function buildGeneratedSubmissions(
  groupCount: number,
): OutcomeReadySubmission[] {
  const _submissions: OutcomeReadySubmission[] = [];
  let _sequence = 1001;

  for (let _index = 0; _index < groupCount; _index++) {
    const _documents: OutcomeDocument[] = [];
    const _size = 3 + (_index % 3);
    for (let _offset = 0; _offset < _size; _offset++) {
      _documents.push(buildGeneratedDocument(_sequence));
      _sequence += 1;
    }

    const _groupKey = 1001 + _index;
    const _protocolId = `PROTO-2025-${101 + _index}`;
    _submissions.push({
      workspace_id: `c4f1a2b0-${_groupKey}-4f5a-9c1d-1a2b3c4d${_groupKey}`,
      workspace_name: _protocolId,
      study_name: GENERATED_STUDY_NAMES[_index % GENERATED_STUDY_NAMES.length],
      sponsor_protocol_id: _protocolId,
      document_count: _documents.length,
      documents: _documents,
    });
  }

  return _submissions;
}

/**
 * Builds the generated standalone documents appended after the curated one.
 *
 * @param count - Number of standalone documents to generate.
 * @returns Deterministic {@link OutcomeStandaloneDocument} rows (ids start at 1901).
 */
function buildGeneratedStandaloneDocuments(
  count: number,
): OutcomeStandaloneDocument[] {
  const _documents: OutcomeStandaloneDocument[] = [];
  for (let _index = 0; _index < count; _index++) {
    const _protocolId = `PROTO-2025-${201 + _index}`;
    _documents.push({
      ...buildGeneratedDocument(1901 + _index),
      study_name: GENERATED_STUDY_NAMES[_index % GENERATED_STUDY_NAMES.length],
      sponsor_protocol_id: _protocolId,
    });
  }
  return _documents;
}

/** Full submission list: 3 curated groups followed by 24 generated ones (27 total). */
const ALL_SUBMISSIONS: OutcomeReadySubmission[] = [
  ...CURATED_SUBMISSIONS,
  ...buildGeneratedSubmissions(24),
];

/** Full standalone list: the curated row followed by 3 generated ones (4 total). */
const ALL_STANDALONE_DOCUMENTS: OutcomeStandaloneDocument[] = [
  ...CURATED_STANDALONE_DOCUMENTS,
  ...buildGeneratedStandaloneDocuments(3),
];

/**
 * Mock payload for the Dashboard Outcomes Ready API.
 *
 * A high-volume fixture for the card's infinite scroll: 27 submission groups
 * plus 4 standalone documents — 109 documents in total. The first 3 groups and
 * the first standalone document are the original hand-crafted Figma entries;
 * the rest are deterministic generated volume.
 *
 * `document_count` and `submission_count` are computed from the arrays, so the
 * totals always mimic the data even as the fixture grows. Consumers wanting a
 * 25-document page slice should go through `paginateOutcomeReadyMock` instead
 * of reading this constant directly.
 *
 * @remarks The standalone document shape is unverified against the back end —
 * see `OutcomeStandaloneDocument`.
 */
export const OUTCOME_READY_MOCK: OutcomeReadyResponse = {
  document_count:
    ALL_SUBMISSIONS.reduce(
      (total, submission) => total + submission.documents.length,
      0,
    ) + ALL_STANDALONE_DOCUMENTS.length,
  submission_count: ALL_SUBMISSIONS.length,
  submissions: ALL_SUBMISSIONS,
  standalone_documents: ALL_STANDALONE_DOCUMENTS,
};
